import React, { useState } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent } from "../components/ui/dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { Edit } from 'lucide-react';
import NegotiationModal from './NegotiationModal';

export default function SortableItem({ id, item, onEdit, isDragging: isDraggingOverlay = false }) {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  const sortable = useSortable({ 
    id,
    data: {
      type: 'item',
      item,
      columnId: item?.status
    },
    disabled: isDraggingOverlay
  });

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = sortable;

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.3 : 1,
  };
  
  // Calcular o valor total dos veículos
  const totalValue = item.vehicles?.reduce((sum, vehicle) => {
    return sum + (vehicle.comercial?.valorVenda || 0);
  }, 0) || 0;
  
  // Determina prioridade baseada no valor total
  const getPriority = () => {
    if (totalValue > 100000) return 'high';
    if (totalValue > 50000) return 'medium';
    return 'low';
  };
  
  const priority = getPriority();
  
  // Função para gerar texto do tooltip com as placas
  const getVehicleTooltip = () => {
    if (!item.vehicles || item.vehicles.length === 0) return t('sortableItem.tooltip.noVehicles');
    
    return item.vehicles.map((vehicle, index) => {
      const placa = vehicle.placa || t('sortableItem.tooltip.noPlate');
      const modelo = vehicle.infoTecnica?.modelo || vehicle.modelo || '';
      const marca = vehicle.infoTecnica?.marca || vehicle.marca || '';
      
      let displayText = placa;
      if (marca && modelo) {
        displayText += ` (${marca} ${modelo})`;
      } else if (modelo) {
        displayText += ` (${modelo})`;
      } else if (marca) {
        displayText += ` (${marca})`;
      }
      
      return displayText;
    }).join('\n');
  };
  
  // Função para exibir texto resumido dos veículos
  const getVehicleDisplayText = () => {
    if (!item.vehicles || item.vehicles.length === 0) return t('sortableItem.display.noVehicle');
    
    const firstVehicle = item.vehicles[0];
    
    // Se o veículo vier apenas como ID (string), retornar mensagem apropriada
    if (typeof firstVehicle === 'string') {
      return `ID: ${firstVehicle}`;
    }
    
    // Priorizar placa, depois modelo
    const display = firstVehicle.placa || 
                   firstVehicle.infoTecnica?.modelo || 
                   firstVehicle.modelo || 
                   t('sortableItem.display.noIdentification');
    
    if (item.vehicles.length === 1) {
      return display;
    } else {
      return t('sortableItem.display.multipleVehicles', { vehicle: display, count: item.vehicles.length - 1 });
    }
  };
  
  const handleOpenModal = () => {
    // If onEdit is provided from parent, use that instead of local modal
    if (onEdit) {
      onEdit(id);
    } else {
      setIsModalOpen(true);
    }
  };
  
  const handleCloseModal = (shouldRefresh = false) => {
    setIsModalOpen(false);
    // Se precisar recarregar os dados após fechar o modal
    if (shouldRefresh && window.refreshNegotiations) {
      window.refreshNegotiations();
    }
  };

  // Handle card click
  const handleCardClick = (e) => {
    // Se o clique não foi em um botão, abre o modal
    if (!e.target.closest('button')) {
      handleOpenModal();
    }
  };

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        transition={{ duration: 0.2 }}
      >
        <Card
          ref={isDraggingOverlay ? undefined : setNodeRef}
          style={isDraggingOverlay ? undefined : style}
          className={`mb-3 border transition-all ${
            isDraggingOverlay 
              ? 'shadow-2xl rotate-3 cursor-grabbing scale-105' 
              : 'hover:shadow-md'
          } ${
            priority === 'high' 
              ? 'border-red-200 hover:border-red-300' 
              : priority === 'medium'
                ? 'border-yellow-200 hover:border-yellow-300'
                : 'border-border hover:border-primary/20'
          }`}
          onClick={isDraggingOverlay ? undefined : handleCardClick}
        >
          <div className="p-4 space-y-4">
            <div className="flex items-start justify-between">
              <div 
                className="flex items-center space-x-3 flex-1" 
                {...(isDraggingOverlay ? {} : attributes)}
                {...(isDraggingOverlay ? {} : listeners)}
                style={{ cursor: isDraggingOverlay ? 'grabbing' : 'grab' }}
              >
                <div>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div>
                          <p className="font-medium text-sm cursor-help">
                            {getVehicleDisplayText()}
                          </p>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent>
                        <div className="text-sm whitespace-pre-line max-w-xs">
                          {getVehicleTooltip()}
                        </div>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                  <p className="text-xs text-muted-foreground">
                    {item.buyerId?.nomeFantasia || t('sortableItem.display.noBuyer')}
                  </p>
                </div>
              </div>
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-8 w-8 text-muted-foreground hover:text-foreground shrink-0 ml-2"
                onClick={handleOpenModal}
              >
                <Edit className="h-4 w-4" />
              </Button>
            </div>

            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">
                {t('sortableItem.display.value')}: {new Intl.NumberFormat('pt-BR', {
                  style: 'currency',
                  currency: 'BRL'
                }).format(totalValue)}
              </span>
            </div>
          </div>
        </Card>
      </motion.div>

      {/* Only render the dialog if we're using local modal management */}
      {!onEdit && (
        <Dialog open={isModalOpen} onOpenChange={(open) => !open && handleCloseModal()}>
          <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto">
            <NegotiationModal
              negotiationId={id}
              onSave={handleCloseModal}
            />
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
